import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Database, RefreshCw, Loader2, Search, AlertTriangle, Copy, Check } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface EngagementDebuggerProps {
  accessToken: string;
  currentUser: any;
}

export function EngagementDebugger({ accessToken, currentUser }: EngagementDebuggerProps) {
  const [loading, setLoading] = useState(false);
  const [repairingCompanyId, setRepairingCompanyId] = useState<string | null>(null);
  const [allEngagements, setAllEngagements] = useState<any[]>([]);
  const [engagementsFromCompanyData, setEngagementsFromCompanyData] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [firmEngagements, setFirmEngagements] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [repairMessage, setRepairMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call the engagements endpoint
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/engagements`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFirmEngagements(data.engagements || []);
        console.log('[ENGAGEMENT_DEBUGGER] Firm engagements:', data.engagements);
        console.log('[ENGAGEMENT_DEBUGGER] Debug info:', data.debug);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch engagements');
      }

      // Also try to get ALL engagements (if endpoint exists)
      try {
        const allResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/debug/all-engagements`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (allResponse.ok) {
          const allData = await allResponse.json();
          setAllEngagements(allData.engagements || []);
          setEngagementsFromCompanyData(allData.engagementsFromCompanyData || []);
          console.log('[ENGAGEMENT_DEBUGGER] All engagements:', allData.engagements);
          console.log('[ENGAGEMENT_DEBUGGER] Engagements from company_data:', allData.engagementsFromCompanyData);
        }
      } catch (err) {
        // It's ok if this endpoint doesn't exist
        console.log('[ENGAGEMENT_DEBUGGER] Admin endpoint not available');
      }

      // Also try to get ALL companies (if endpoint exists)
      try {
        const allCompaniesResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/debug/all-companies`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (allCompaniesResponse.ok) {
          const allCompaniesData = await allCompaniesResponse.json();
          setAllCompanies(allCompaniesData.companies || []);
          console.log('[ENGAGEMENT_DEBUGGER] All companies:', allCompaniesData.companies);
        }
      } catch (err) {
        // It's ok if this endpoint doesn't exist
        console.log('[ENGAGEMENT_DEBUGGER] Admin endpoint not available');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Unknown error occurred';
      setError(errorMessage);
      console.error('[ENGAGEMENT_DEBUGGER] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const repairCompany = async (companyId: string) => {
    setRepairingCompanyId(companyId);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/debug/repair-company`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ companyId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[ENGAGEMENT_DEBUGGER] Repair result:', data);
        
        let message = `${data.message}`;
        
        // Add debug info if present
        if (data.debugInfo) {
          message += `\n\nDEBUG INFO:`;
          message += `\n• Key: ${data.debugInfo.key || 'N/A'} `;
          message += `\n• Has ID: ${data.debugInfo.hasId}`;
          message += `\n• Has Status: ${data.debugInfo.hasStatus}`;
          message += `\n• Has Template: ${data.debugInfo.hasTemplate}`;
          message += `\n• Has Firm ID: ${data.debugInfo.hasFirmId}`;
          message += `\n• Is Broken: ${data.debugInfo.isBroken}`;
          if (data.debugInfo.actualId !== undefined) {
            message += `\n\nACTUAL VALUES:`;
            message += `\n• ID: ${data.debugInfo.actualId || 'NULL'}`;
            message += `\n• Status: ${data.debugInfo.actualStatus || 'NULL'}`;
            message += `\n• Template: ${data.debugInfo.actualTemplate || 'NULL'}`;
            message += `\n• Firm ID: ${data.debugInfo.actualFirmId || 'NULL'}`;
          }
        }
        
        // Add diagnostic info if present
        if (data.diagnostic) {
          message += `\n\nDIAGNOSTIC:`;
          message += `\n• Total engagements found: ${data.diagnostic.totalEngagementsFound}`;
          message += `\n• Healthy: ${data.diagnostic.healthyCount}`;
          message += `\n• Broken: ${data.diagnostic.brokenCount}`;
          if (data.diagnostic.allKeys && data.diagnostic.allKeys.length > 0) {
            message += `\n• Keys: ${data.diagnostic.allKeys.join(', ')}`;
          }
          if (data.diagnostic.brokenKeys && data.diagnostic.brokenKeys.length > 0) {
            message += `\n• Broken keys: ${data.diagnostic.brokenKeys.join(', ')}`;
          }
        }
        
        if (data.wasRepaired) {
          message += `\n\nEngagement was repaired.`;
          if (data.oldKey && data.newKey) {
            message += `\n   Old key: ${data.oldKey}`;
            message += `\n   New key: ${data.newKey}`;
          }
          if (data.deletedCount !== undefined) {
            message += `\n   Deleted ${data.deletedCount} broken engagement(s)`;
          }
        }
        
        message += `\n\nRefresh the page to see the updated engagement.`;
        
        setRepairMessage(message);
        setRepairDialogOpen(true);
        // Refresh the diagnostic
        await runDiagnostic();
      } else {
        const errorData = await response.json();
        alert(`Repair failed: ${errorData.error}`);
      }
    } catch (err: any) {
      console.error('[ENGAGEMENT_DEBUGGER] Repair error:', err);
      alert(`Repair failed: ${err.message}`);
    } finally {
      setRepairingCompanyId(null);
    }
  };

  const copyTemplateCIs = async (companyId: string) => {
    setRepairingCompanyId(companyId);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/debug/copy-template-cis`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ companyId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const message = `${data.message}\n\nPlatform: ${data.platformTemplate}\nConfig Items: ${data.ciCount}\n\nRefresh the page to see discovery questions in the Discovery Grid.`;
        setRepairMessage(message);
        setRepairDialogOpen(true);
        await runDiagnostic();
      } else {
        const errorData = await response.json();
        alert(`Failed to copy template CIs: ${errorData.error}\n\n${errorData.hint || ''}`);
      }
    } catch (err: any) {
      console.error('[ENGAGEMENT_DEBUGGER] Copy CIs error:', err);
      alert(`Failed to copy template CIs: ${err.message}`);
    } finally {
      setRepairingCompanyId(null);
    }
  };

  const fixCompanyTemplate = async (companyId: string, newTemplate: string) => {
    setRepairingCompanyId(companyId);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/debug/fix-company-template`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ companyId, platformTemplate: newTemplate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const message = `${data.message}\\n\\nEngagements Updated: ${data.engagementsUpdated}\\n\\nNow click "Copy Template CIs" to load the discovery questions.`;
        setRepairMessage(message);
        setRepairDialogOpen(true);
        await runDiagnostic();
      } else {
        const errorData = await response.json();
        alert(`Failed to fix template: ${errorData.error}`);
      }
    } catch (err: any) {
      console.error('[ENGAGEMENT_DEBUGGER] Fix template error:', err);
      alert(`Failed to fix template: ${err.message}`);
    } finally {
      setRepairingCompanyId(null);
    }
  };

  // Find companies without engagements
  const companiesWithoutEngagements = allCompanies.filter(company => {
    // Only show companies from THIS firm
    const userFirmId = currentUser.consultingFirmId || currentUser.firmId;
    if (company.consultingFirmId !== userFirmId) {
      return false;
    }
    return !firmEngagements.some(eng => eng.companyId === company.id);
  });

  // Find broken engagements (missing critical fields)
  const brokenEngagements = firmEngagements.filter(eng => {
    return !eng.id || !eng.status || !eng.platformTemplate || !eng.consultingFirmId;
  });

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="size-5 text-blue-600" />
          Engagement Debugger
        </CardTitle>
        <CardDescription>
          Debug tool to see what engagements exist in the database
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current User Info */}
        <div className="bg-white p-3 rounded border">
          <h4 className="font-medium mb-2 text-sm">Current User Info:</h4>
          <div className="space-y-1 text-xs font-mono">
            <div><strong>User ID:</strong> {currentUser.id}</div>
            <div><strong>Email:</strong> {currentUser.email}</div>
            <div><strong>Role:</strong> {currentUser.role}</div>
            <div><strong>Consulting Firm ID:</strong> {currentUser.consultingFirmId || currentUser.firmId || 'N/A'}</div>
            <div><strong>Firm Name:</strong> {currentUser.firmName || 'N/A'}</div>
          </div>
        </div>

        <Button
          onClick={runDiagnostic}
          disabled={loading}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              Scanning Database...
            </>
          ) : (
            <>
              <Search className="size-4 mr-2" />
              Scan for Engagements
            </>
          )}
        </Button>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Firm Engagements */}
        {!loading && firmEngagements.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              Your Firm's Engagements
              <Badge>{firmEngagements.length}</Badge>
            </h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {firmEngagements.map((engagement, idx) => (
                <div key={idx} className="bg-white p-3 rounded border text-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium">{engagement.companyName || <span className="text-red-500">(NO NAME)</span>}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        Company ID: {engagement.companyId || <span className="text-red-500">(NO ID)</span>}
                      </div>
                    </div>
                    <Badge variant={
                      engagement.healthStatus === 'healthy' ? 'default' :
                      engagement.healthStatus === 'at-risk' ? 'secondary' :
                      'destructive'
                    }>
                      {engagement.healthStatus || 'unknown'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Engagement ID:</span>
                      <div className="font-mono">{engagement.id || <span className="text-red-500">(NO ID)</span>}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Template:</span>
                      <div>{engagement.platformTemplate || <span className="text-red-500">(NO TEMPLATE)</span>}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <div>{engagement.status || <span className="text-red-500">(NO STATUS)</span>}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Progress:</span>
                      <div>{engagement.completedItems || 0}/{engagement.totalItems || 0}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <div>{engagement.createdAt ? new Date(engagement.createdAt).toLocaleString() : 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Consulting Firm:</span>
                      <div className="font-mono text-xs break-all">{engagement.consultingFirmId || <span className="text-red-500">(NO FIRM)</span>}</div>
                    </div>
                  </div>
                  {/* Template Warning */}
                  {engagement.platformTemplate === 'custom' && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Company is set to "Custom" template which has no questions. Fix this first.
                      </AlertDescription>
                    </Alert>
                  )}
                  {/* Action Buttons */}
                  <div className="mt-3 flex gap-2">
                    {engagement.platformTemplate === 'custom' && (
                      <Button
                        onClick={() => fixCompanyTemplate(engagement.companyId, 'atlassian')}
                        disabled={repairingCompanyId === engagement.companyId}
                        variant="default"
                        size="sm"
                        className="flex-1"
                      >
                        {repairingCompanyId === engagement.companyId ? (
                          <>
                            <Loader2 className="size-4 mr-2 animate-spin" />
                            Fixing...
                          </>
                        ) : (
                          <>
                            Fix Template to Atlassian
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      onClick={() => copyTemplateCIs(engagement.companyId)}
                      disabled={repairingCompanyId === engagement.companyId || engagement.platformTemplate === 'custom'}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {repairingCompanyId === engagement.companyId ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="size-4 mr-2" />
                          Copy Template CIs
                        </>
                      )}
                    </Button>
                  </div>
                  {/* Show raw JSON for debugging */}
                  <details className="mt-2">
                    <summary className="text-xs text-blue-600 cursor-pointer">Show Raw Data</summary>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto max-h-48 overflow-y-auto">
                      {JSON.stringify(engagement, null, 2)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Engagements (if available) */}
        {!loading && allEngagements.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              All Engagements in Database
              <Badge variant="secondary">{allEngagements.length}</Badge>
            </h4>
            <div className="bg-white p-3 rounded border text-xs font-mono max-h-48 overflow-y-auto">
              {allEngagements.map((eng, idx) => (
                <div key={idx} className="py-1 border-b last:border-b-0">
                  {eng.companyName} - Firm: {eng.consultingFirmId?.substring(0, 20)}...
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Companies (if available) */}
        {!loading && allCompanies.length > 0 && (() => {
          const userFirmId = currentUser.consultingFirmId || currentUser.firmId;
          const firmCompanies = allCompanies.filter(c => c.consultingFirmId === userFirmId);
          
          return (
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                Your Firm's Companies in Database
                <Badge variant="secondary">{firmCompanies.length}</Badge>
                <span className="text-xs text-muted-foreground">({allCompanies.length} total in system)</span>
              </h4>
              <div className="bg-white p-3 rounded border text-xs font-mono max-h-48 overflow-y-auto">
                {firmCompanies.map((company, idx) => (
                  <div key={idx} className="py-1 border-b last:border-b-0">
                    {company.name} - ID: {company.id}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Engagements from Company Data (if available) */}
        {!loading && engagementsFromCompanyData.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              Engagements from Company Data
              <Badge variant="secondary">{engagementsFromCompanyData.length}</Badge>
            </h4>
            <div className="bg-white p-3 rounded border text-xs font-mono max-h-48 overflow-y-auto">
              {engagementsFromCompanyData.map((eng, idx) => (
                <div key={idx} className="py-1 border-b last:border-b-0">
                  {eng.companyName} - Firm: {eng.consultingFirmId?.substring(0, 20)}...
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && firmEngagements.length === 0 && !error && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>No engagements found for your firm.</strong>
              <br />
              This means either:
              <ul className="list-disc pl-4 mt-2 space-y-1">
                <li>The company creation didn't create an engagement</li>
                <li>The engagement has a different consultingFirmId</li>
                <li>The /consultant/engagements endpoint isn't working</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Companies without engagements */}
        {!loading && companiesWithoutEngagements.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              Companies without Engagements
              <Badge variant="destructive">{companiesWithoutEngagements.length}</Badge>
            </h4>
            <div className="bg-white p-3 rounded border text-xs font-mono max-h-48 overflow-y-auto">
              {companiesWithoutEngagements.map((company, idx) => (
                <div key={idx} className="py-1 border-b last:border-b-0">
                  {company.name} - ID: {company.id}
                  <Button
                    onClick={() => repairCompany(company.id)}
                    disabled={repairingCompanyId === company.id}
                    className="ml-2"
                    size="sm"
                  >
                    {repairingCompanyId === company.id ? (
                      <>
                        <Loader2 className="size-4 mr-2 animate-spin" />
                        Repairing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-4 mr-2" />
                        Repair
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Broken engagements */}
        {!loading && brokenEngagements.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              Broken Engagements (Missing Fields)
              <Badge variant="destructive">{brokenEngagements.length}</Badge>
            </h4>
            <Alert variant="destructive" className="mb-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                These engagements are missing critical fields (id, status, template, or consultingFirmId).
                Click "Repair" to fix them.
              </AlertDescription>
            </Alert>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {brokenEngagements.map((eng, idx) => (
                <div key={idx} className="bg-white p-3 rounded border">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">{eng.companyName || '(NO NAME)'}</div>
                      <div className="text-xs text-red-500 font-mono">
                        Missing: {[
                          !eng.id && 'ID',
                          !eng.status && 'Status',
                          !eng.platformTemplate && 'Template',
                          !eng.consultingFirmId && 'Firm'
                        ].filter(Boolean).join(', ')}
                      </div>
                    </div>
                    <Button
                      onClick={() => repairCompany(eng.companyId)}
                      disabled={repairingCompanyId === eng.companyId}
                      variant="destructive"
                      size="sm"
                    >
                      {repairingCompanyId === eng.companyId ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Repairing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="size-4 mr-2" />
                          Repair
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Repair Result Dialog */}
      <Dialog open={repairDialogOpen} onOpenChange={setRepairDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Repair Result</DialogTitle>
            <DialogDescription>
              Details about the repair operation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <pre className="bg-gray-100 p-4 rounded text-sm whitespace-pre-wrap font-mono select-text">
              {repairMessage}
            </pre>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(repairMessage);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                variant="outline"
              >
                {copied ? (
                  <>
                    <Check className="size-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="size-4 mr-2" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
              <Button onClick={() => setRepairDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}